import { Button, Group, Box, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { string, object, number, array } from "yup";
import {
  CreateRoomInput,
  useCreateRoomMutation,
  useGetLocationLazyQuery,
} from "src/generated/graphql";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import MultiSelect from "src/components/MultiSelect";
import NumberInput from "src/components/NumberInput/NumberInput";
import { useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { RoomData } from "../List";

export interface RoomFormProps {
  refetchData: () => void;
  handleClose: () => void;
  roomData?: RoomData;
  isAllowWrite?: boolean;
}

export const roomFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  description: string().max(200).notRequired(),
  totalCapacity: number().required("Max Occupancy is required"),
  services: array()
    .min(1, "at least 1 supported services is required")
    .required("Supported Services is required"),
});

const CreateRoomForm = ({ refetchData, handleClose }: RoomFormProps) => {
  const theme = useMantineTheme();
  const { currentLocation } = useContext(AppContext);
  const [getLocation, { data: currentLocationInfo }] =
    useGetLocationLazyQuery();

  useEffect(() => {
    currentLocation && getLocation({ variables: { id: currentLocation?.id } });
  }, [currentLocation?.id]);

  const [createRoom, { loading }] = useCreateRoomMutation({
    onCompleted: () => {
      handleClose();
      refetchData();
    },
  });

  const form = useForm<CreateRoomInput>({
    validate: yupResolver(roomFormSchema),
  });

  const onSubmit = async ({
    services,
    ...restValues
  }: Omit<CreateRoomInput, "location">) => {
    await createRoom({
      variables: {
        data: {
          location: {
            id: currentLocation?.id!,
          },
          services: services.map((service) => ({
            id: service as unknown as string,
          })),
          ...restValues,
        },
      },
    });
  };

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
        <TextInput withAsterisk label="Name" {...form.getInputProps("name")} />
        <Textarea label="Description" {...form.getInputProps("description")} />
        <NumberInput
          withAsterisk
          label="Max Occupancy"
          type="number"
          {...form.getInputProps("totalCapacity")}
          min={1}
        />
        <MultiSelect
          data={
            (currentLocationInfo?.locationOne &&
              currentLocationInfo.locationOne.locationServices.map((val) => ({
                value: val.id,
                label: val.organizationService.name,
              }))) ||
            []
          }
          label="Supported Services"
          {...form.getInputProps("services")}
        />

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={handleClose}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loading}
          >
            Create
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default CreateRoomForm;
